import { watch, onMounted } from "vue";
import { useForm } from 'vee-validate';
import { getFormFromSchema, setObjectIntoFormBySchema } from "../../utils.js";
import { modifyPatchCS, modifyPatchPark } from "../../remote/park.js";

/*
Code to initialize the state:
const useAlerts = defineModel('useAlerts');
const loadingOverlay = defineModel('loadingOverlay');
const props = defineProps(['originalPark']);
const { originalPark } = toRefs(props);
const emit = defineEmits(['updated']);
 */

/**
 * Composable to create the base status for a ModifyPark Child Panel.
 * The parameters that use a define macro cannot be called inside the composable, that's why the parameters.
 *
 * @param useAlerts from `const useAlerts = defineModel('useAlerts')`
 * @param loadingOverlay from `const loadingOverlay = defineModel('loadingOverlay')`
 * @param originalPark from `const props = defineProps(['originalPark']);const { originalPark } = toRefs(props)`
 * @param schema built with yup.object({key: yup_validator})
 * @param emit Vue emit objects with an event called "updated" defined
 * @param skipSetFields optional, if you need to skip the default setter for some fields when a new park is opened
 * @param specialSetter optional, if you need to perform manual settings when a new park is opened
 * @param editPayload optional, if you need to perform transformations on the payload before submit
 * @returns {{form: *, submit: *}}
 */
export function useModifyParkBase(
  useAlerts, loadingOverlay, originalPark, schema, emit,
  skipSetFields = undefined,
  specialSetter = undefined,
  editPayload = undefined,
) {
  // Defaults
  if (skipSetFields === undefined) skipSetFields = [];
  if (specialSetter === undefined) specialSetter = (_form, _newValue) => {
  };
  if (editPayload === undefined) editPayload = (_values) => {
  };

  const { handleSubmit } = useForm({
    validationSchema: schema
  });

  const form = getFormFromSchema(schema);

  // In theory the watch is enough because for now impossible to have the Component loaded with already a park onMounted.
  watch(originalPark, (newValue) => {
    setObjectIntoFormBySchema(newValue, form, schema, skipSetFields);
    specialSetter(form, newValue);
  });

  // But JUST TO BE SURE, let's make a onMounted too, that just get skipped if the prop is null
  onMounted(() => {
    if (originalPark.value !== null) {
      setObjectIntoFormBySchema(originalPark.value, form, schema, skipSetFields);
      specialSetter(form, originalPark.value);
    }
  });

// noinspection JSValidateTypes
  const submit = handleSubmit(async (values) => {
    editPayload(values);
    console.log(values);
    // Values is almost a PATCH payload!
    loadingOverlay.value = true;
    // Modify Park via PATCH
    try {
      await modifyPatchPark(useAlerts, values, originalPark.value.id);
      emit('updated');
    } catch (e) {
      useAlerts.value.errorAlert('Error during Park save', e.toString());
    } finally {
      loadingOverlay.value = false;
    }
  });

  return { form, submit };
}


/**
 * Composable to create the base status for a ModifyPark Modify Charging Station Child Panel.
 * The parameters that use a define macro cannot be called inside the composable, that's why the parameters.
 *
 * @param useAlerts from `const useAlerts = defineModel('useAlerts')`
 * @param loadingOverlay from `const loadingOverlay = defineModel('loadingOverlay')`
 * @param cs from `const props = defineProps(['cs']);const { cs } = toRefs(props)`
 * @param schema built with yup.object({key: yup_validator})
 * @param emit Vue emit objects with an event called "updated" defined
 * @param skipSetFields optional, if you need to skip the default setter for some fields when a new park is opened
 * @param specialSetter optional, if you need to perform manual settings when a new park is opened
 * @param editPayload optional, if you need to perform transformations on the payload before submit
 * @returns {{form: *, submit: *}}
 */
export function useModifyCSBase(
  useAlerts, loadingOverlay, cs, schema, emit,
  skipSetFields = undefined,
  specialSetter = undefined,
  editPayload = undefined,
) {
  // Defaults
  if (skipSetFields === undefined) skipSetFields = [];
  if (specialSetter === undefined) specialSetter = (_form, _newValue) => {
  };
  if (editPayload === undefined) editPayload = (_values) => {
  };

  const { handleSubmit } = useForm({
    validationSchema: schema
  });

  const form = getFormFromSchema(schema);

  // In theory the watch is enough because for now impossible to have the Component loaded with already a park onMounted.
  watch(cs, (newValue) => {
    setObjectIntoFormBySchema(newValue, form, schema, skipSetFields);
    specialSetter(form, newValue);
  });

  // But JUST TO BE SURE, let's make a onMounted too, that just get skipped if the prop is null
  onMounted(() => {
    if (cs.value !== null) {
      setObjectIntoFormBySchema(cs.value, form, schema, skipSetFields);
      specialSetter(form, cs.value);
    }
  });

// noinspection JSValidateTypes
  const submit = handleSubmit(async (values) => {
    editPayload(values);
    console.log(values);
    // Values is almost a PATCH payload!
    loadingOverlay.value = true;
    // Modify CS via PATCH
    try {
      await modifyPatchCS(useAlerts, values, cs.value['park'], cs.value['park_bnum']);
      emit('updated');
    } catch (e) {
      useAlerts.value.errorAlert('Error during CS save', e.toString());
    } finally {
      loadingOverlay.value = false;
    }
  });

  return { form, submit };
}
